import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  HStack,
  Icon,
  Tooltip,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { orderService } from "../../../../services/orderService";
import { MdInfo, MdPeople, MdTrendingUp, MdAttachMoney, MdRefresh } from "react-icons/md";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";

export default function CustomerAnalytics() {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const bgHover = useColorModeValue("gray.50", "navy.700");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await orderService.getCustomerAnalytics();
      
      if (response.success) {
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error('Müşteri analizi getirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Müşteri Analizi
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }
  
  if (!analyticsData) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Müşteri Analizi
        </Text>
        <Center py={8}>
          <Text color="gray.500">Veri yüklenemedi</Text>
        </Center>
      </Card>
    );
  }
  
  const { summary, topCustomers, topValueCustomers, monthlyGrowth, genderDistribution = {}, ageGroups = {} } = analyticsData;
  
  // Cinsiyet dağılımı için pie chart verisi
  const genderData = [
    { name: 'Erkek', value: genderDistribution.male || 0, color: '#0088FE' },
    { name: 'Kadın', value: genderDistribution.female || 0, color: '#FF6B9D' },
    { name: 'Diğer', value: genderDistribution.other || 0, color: '#8884d8' },
    { name: 'Belirtilmemiş', value: genderDistribution.unknown || 0, color: '#82ca9d' }
  ].filter(item => item.value > 0);
  
  // Yaş grupları için bar chart verisi
  const ageData = [
    { ageGroup: '18-25', count: ageGroups['18-25'] || 0 },
    { ageGroup: '26-35', count: ageGroups['26-35'] || 0 },
    { ageGroup: '36-45', count: ageGroups['36-45'] || 0 },
    { ageGroup: '46-55', count: ageGroups['46-55'] || 0 },
    { ageGroup: '56-65', count: ageGroups['56-65'] || 0 },
    { ageGroup: '65+', count: ageGroups['65+'] || 0 },
    { ageGroup: 'Bilinmiyor', count: ageGroups.unknown || 0 }
  ];
  
  return (
    <Box>
      {/* Özet İstatistikler */}
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4} mb={6}>
        <Card p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">Toplam Müşteri</StatLabel>
            <StatNumber fontSize="2xl" color={textColor}>{summary.totalCustomers}</StatNumber>
            <StatHelpText fontSize="xs">
              <Icon as={MdPeople} mr={1} />
              Tüm müşteriler
            </StatHelpText>
          </Stat>
        </Card>
        
        <Card p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">Yeni Müşteriler</StatLabel>
            <StatNumber fontSize="2xl" color="green.400">
              {summary.newCustomers}
            </StatNumber>
            <StatHelpText fontSize="xs">
              <Icon as={MdTrendingUp} mr={1} />
              Son 30 gün
            </StatHelpText>
          </Stat>
        </Card>
        
        <Card p={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500">Tekrar Eden</StatLabel>
            <StatNumber fontSize="2xl" color="blue.400">
              {summary.returningCustomers}
            </StatNumber>
            <StatHelpText fontSize="xs">
              <Icon as={MdRefresh} mr={1} />
              2+ sipariş
            </StatHelpText>
          </Stat>
        </Card>
      </SimpleGrid>
      
      {/* Müşteri Büyüme Trendi */}
      <Card mb={6}>
        <Flex direction="column" h="100%" justify="space-between">
          <HStack spacing={2} mb="4">
            <Text color={textColor} fontSize="lg" fontWeight="700">
              Müşteri Büyüme Trendi
            </Text>
            <Tooltip 
              label="Son 12 ayda yeni müşteri sayısı" 
              placement="top"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="sm"
              openDelay={300}
            >
              <Box display="inline-block">
                <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
              </Box>
            </Tooltip>
          </HStack>
          
          <Box flex="1">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={10}
                />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="newCustomers" fill="#0088FE" name="Yeni Müşteriler" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Flex>
      </Card>
      
      {/* Müşteri Segmentasyonu */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        <Card>
          <Flex direction="column" h="100%" justify="space-between">
            <HStack spacing={2} mb="4">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Müşteri Segmentasyonu
              </Text>
              <Tooltip 
                label="Müşterilerin aktivite durumuna göre dağılımı" 
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                openDelay={300}
              >
                <Box display="inline-block">
                  <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
                </Box>
              </Tooltip>
            </HStack>
            
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between" p={3} borderRadius="md" bg={bgStat}>
                <Text fontSize="sm">Aktif Müşteriler</Text>
                <Badge colorScheme="green" fontSize="sm">
                  {summary.activeCustomers}
                </Badge>
              </Flex>
              
              <Flex justify="space-between" p={3} borderRadius="md" bg={bgStat}>
                <Text fontSize="sm">Pasif Müşteriler</Text>
                <Badge colorScheme="gray" fontSize="sm">
                  {summary.inactiveCustomers}
                </Badge>
              </Flex>
              
              <Flex justify="space-between" p={3} borderRadius="md" bg={bgStat}>
                <Text fontSize="sm">Ortalama Sipariş Değeri</Text>
                <Text fontSize="sm" fontWeight="600" color={textColor}>
                  ₺{summary.avgOrderValuePerCustomer.toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Text>
              </Flex>
            </VStack>
          </Flex>
        </Card>
        
        {/* En Aktif Müşteriler */}
        <Card>
          <Flex direction="column" h="100%" justify="space-between">
            <HStack spacing={2} mb="4">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                En Aktif Müşteriler
              </Text>
              <Tooltip 
                label="En çok sipariş veren müşteriler" 
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                openDelay={300}
              >
                <Box display="inline-block">
                  <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
                </Box>
              </Tooltip>
            </HStack>
            
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor} color={textColor}>Müşteri</Th>
                    <Th borderColor={borderColor} color={textColor} isNumeric>Sipariş</Th>
                    <Th borderColor={borderColor} color={textColor} isNumeric>Toplam</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topCustomers.slice(0, 5).map((customer, index) => (
                    <Tr key={index} _hover={{ bg: bgHover }}>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                          {customer.customerName}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        <Text fontSize="sm" color={textColor}>
                          {customer.totalOrders}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                          ₺{customer.totalSpent.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Flex>
        </Card>
      </SimpleGrid>
      
      {/* Cinsiyet ve Yaş Analizi */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
        {/* Cinsiyet Dengesi */}
        <Card>
          <Flex direction="column" h="100%" justify="space-between">
            <HStack spacing={2} mb="4">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Cinsiyet Dengesi
              </Text>
              <Tooltip 
                label="Müşterilerin cinsiyet dağılımı" 
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                openDelay={300}
              >
                <Box display="inline-block">
                  <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
                </Box>
              </Tooltip>
            </HStack>
            
            {genderData.length > 0 ? (
              <>
                <Box flex="1">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={genderData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {genderData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                
                <VStack spacing={2} mt={4} align="stretch">
                  {genderData.map((item, index) => (
                    <Flex key={index} justify="space-between" p={2} borderRadius="md" bg={bgStat}>
                      <HStack spacing={2}>
                        <Box w={3} h={3} borderRadius="full" bg={item.color} />
                        <Text fontSize="sm">{item.name}</Text>
                      </HStack>
                      <Text fontSize="sm" fontWeight="600" color={textColor}>
                        {item.value} ({((item.value / summary.totalCustomers) * 100).toFixed(1)}%)
                      </Text>
                    </Flex>
                  ))}
                </VStack>
              </>
            ) : (
              <Center py={8}>
                <Text color="gray.500">Cinsiyet verisi bulunmuyor</Text>
              </Center>
            )}
          </Flex>
        </Card>
        
        {/* Yaş Grupları */}
        <Card>
          <Flex direction="column" h="100%" justify="space-between">
            <HStack spacing={2} mb="4">
              <Text color={textColor} fontSize="lg" fontWeight="700">
                Yaş Grupları
              </Text>
              <Tooltip 
                label="Müşterilerin yaş gruplarına göre dağılımı" 
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="sm"
                openDelay={300}
              >
                <Box display="inline-block">
                  <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
                </Box>
              </Tooltip>
            </HStack>
            
            <Box flex="1">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="ageGroup" 
                    fontSize={11}
                  />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#00C49F" name="Müşteri Sayısı" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            
            <VStack spacing={2} mt={4} align="stretch">
              {ageData.filter(item => item.count > 0).map((item, index) => (
                <Flex key={index} justify="space-between" p={2} borderRadius="md" bg={bgStat}>
                  <Text fontSize="sm">{item.ageGroup} yaş</Text>
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    {item.count} ({((item.count / summary.totalCustomers) * 100).toFixed(1)}%)
                  </Text>
                </Flex>
              ))}
            </VStack>
          </Flex>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
